import { DTMF_KEYS , ACTION_COLORS} from '../../../../../utilities/constant'
import { toast } from 'react-toastify';


const getDtmfIndex = (edge) => {
    const key = edge.sourceHandle?.split('-').pop();
    const idx = DTMF_KEYS.indexOf(key);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// Recursive function to calculate left-most path duration
const calculateLeftPathDuration = (node, nodesArray, edges, visited = new Set()) => {
    if (!node) return 0;

    // Always add the node's own duration first
    let duration = 0;
    if (node.data?.verb === "play") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "record") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
        duration += Number(node.data?.timeout ?? 0);
        duration += node.data?.action?.menu?.duration ?? 0;
    }

    // Avoid infinite loops by marking visited AFTER counting current node
    if (visited.has(node.id)) return duration;
    visited.add(node.id);

    const edgeData = edges.filter(e => e.source === node.id);

    if (node.data?.verb === "dtmf") {
        const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
        if (sortedEdgeData.length > 0) {
            const firstEdge = sortedEdgeData[0];
            const targetNode = nodesArray.find(n => n.id === firstEdge.target);
            duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
        }
    } else if (edgeData.length > 0) {
        const firstEdge = edgeData[0];
        const targetNode = nodesArray.find(n => n.id === firstEdge.target);
        duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
    }

    return duration;
};

export const transformNodes = ({
    nodesArray,
    edges,
    flowName,
    scheduleData,
    isGatherNode,
    userId,
    smsWebhookFlag,
    isMissed = false
}) => {
    const cid = userId;
    const nodeIdMap = new Map(nodesArray.map((node, index) => [node.id, index]));

    // Start from the first node in nodesArray (assume it's root)
    const totalDuration = calculateLeftPathDuration(nodesArray[0], nodesArray, edges);
    const dtmfTargetNodeIds = new Set();

    const transformedNodes = nodesArray.reduce((acc, node, index) => {
        const edgeData = edges.filter(obj => obj.source === node.id);
        const nodeId = nodeIdMap.get(node.id);

        const flowJson = { verb: node.data?.verb };

        if (node.data?.verb === "play") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
        }
        if (node.data?.verb === "record") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
            flowJson.record_duration = node.data?.record_duration
        }
        else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
            flowJson.type = node.data?.type;
            flowJson.action = node.data?.action;
            flowJson.numDigits = node.data?.numDigits;
            flowJson.timeout = node.data?.timeout;
            flowJson.numSources = node.data?.numSources;
            flowJson.rePrompt = node.data?.rePrompt;
            flowJson.ttsRows = node.data?.ttsRows ?? [];
            flowJson.minValue = node.data?.minValue ?? "";
            flowJson.smsData = node.data?.smsData ?? {};
            flowJson.manualDigits = node.data?.manualDigits ?? ""
        }
        else if (node.data?.verb === "transfer") {
            flowJson.action = node.data?.action;
            flowJson.timeSlots = node.data?.timeSlots ?? [];
            flowJson.defaultNodeId = node.data?.defaultNodeId ?? null;
        }
        else if (node.data?.verb === "default") {
            flowJson.mode = node.data?.mode || "";
            flowJson.action = node.data?.action ?? {};
            flowJson.max_duration = node.data?.max_duration ?? null;
            flowJson.use_media = !!node.data?.use_media;
            flowJson.parentNodeId = node.data?.parentNodeId ?? null;
        }
        else if (node.data?.verb === "hangup") {
            flowJson.mode = node.data?.mode || "hangup";
            flowJson.action = node.data?.action ?? {};
            flowJson.max_duration = node.data?.max_duration ?? null;
        }
        else if (node.data?.verb === "voicemail") {
            flowJson.action = node.data?.action ?? {};
            flowJson.max_duration = node.data?.max_duration ?? null;
            flowJson.use_media = !!node.data?.use_media;
            flowJson.duration = node.data?.action?.duration ?? 0;
        }
        else if (node.data?.verb === "missed") {
            flowJson.mode = node.data?.mode || "";
            flowJson.action = node.data?.action ?? {};
            flowJson.max_duration = node.data?.max_duration ?? null;
        }

        let flowControl = {};

        if (node.data?.verb === "dtmf"  && Object.keys(node.data?.selectedHandles ?? {}).length !== 0) {
            const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
            sortedEdgeData.forEach(edge => {
                const key = edge.sourceHandle?.split('-').pop();
                const targetNumericId = nodeIdMap.get(edge.target);
                const targetNode = nodesArray.find(n => n.id === edge.target);

                if (targetNode.data?.verb === "play" || targetNode.data?.verb === "record") dtmfTargetNodeIds.add(targetNode.id);
                if (!key || targetNumericId === undefined) return;

                flowControl[`dtmf_${key}`] = {
                    flowName,
                    nextNodeId: targetNumericId,
                    isLoop: edge.source === edge.target,
                    parent: node.data?.parent ?? ""
                };
            });
        }
        else if (edgeData.length > 0) {
            const targetNumericId = nodeIdMap.get(edgeData[0].target);
            if (targetNumericId) {
                flowControl.default = {
                    flowName,
                    nextNodeId: targetNumericId,
                    parent: node.data?.parent ?? ""
                };
            }
        }

        acc[nodeId] = {
            id: nodeId,
            flowJson,
            flowRender: { ...node },
            flowControl
        };

        return acc;
    }, {});

    return {
        flowName,
        userId: cid,
        nodes: transformedNodes,
        scheduleData,
        isGatherNode,
        promptDuration: totalDuration,
        smsWebhookFlag,
        ivr_category: isMissed ? "missed" : "normal"
    };
};

const computeTransferCoverage = (slots = []) => {
  return slots.reduce((acc, s) => {
    const start = s.start_time || s.startTime;
    const end = s.end_time || s.endTime;
    if (!start || !end || !start.includes(":") || !end.includes(":")) return acc;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const st = sh * 60 + sm;
    const en = eh * 60 + em;
    if (en > st) acc += en - st;
    return acc;
  }, 0);
};

export const validateIVR = ({ nodes = [], edges = [], flowName, scheduleData, isMissed = false}) => {
  if (!nodes.length) {
      toast.warning("Add at least one node.")
    return false;
  }

  if (!flowName) {
      toast.warning("IVR Name required.")
    return false;
  }

  if (Object.keys(scheduleData).length <= 0) {
    toast.warning("Time Schedule required.")
    return false;
  }

  const selectedDays = Array.isArray(scheduleData?.week_days) ? scheduleData.week_days : [];
  if (!selectedDays.length) {
    toast.warning("Please select at least one day in Time Schedule");
    return false;
  }

  const start = scheduleData?.start_time;
  const end = scheduleData?.end_time;
  const toMinutes = (timeValue) => {
    if (!timeValue || !String(timeValue).includes(":")) return NaN;
    const [hours, minutes] = String(timeValue).split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return (hours * 60) + minutes;
  };

  const startMins = toMinutes(start);
  const endMins = toMinutes(end);
  if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
    toast.warning("Invalid time selected in Time Schedule");
    return false;
  }

  if (endMins <= startMins) {
    toast.warning("End time should be greater than start time");
    return false;
  }

  // 🔹 Build edge maps for O(1) lookups
  const incomingMap = new Map();
  const outgoingMap = new Map();

  for (const edge of edges) {
    if (!incomingMap.has(edge.target)) {
      incomingMap.set(edge.target, []);
    }
    if (!outgoingMap.has(edge.source)) {
      outgoingMap.set(edge.source, []);
    }

    incomingMap.get(edge.target).push(edge);
    outgoingMap.get(edge.source).push(edge);
  }

  const FULL_DAY_MINS = 1439;

  if (isMissed) {
    const allowed = new Set(["missedNode"]);
    for (const node of nodes) {
      const lbl = String(node?.data?.label ?? "").toUpperCase();
      if (!allowed.has(node.type)) {
        toast.warning(`Missed IVR can only contain Missed nodes`);
        return false;
      }
      const mode = node?.data?.mode;
      if (!mode) {
        toast.warning(`Missed node "${lbl}" must have an action selected`);
        return false;
      }
      if (mode === "hangup_with_media" || mode === "voicemail") {
        if (node?.data?.action?.prompt_category && !node?.data?.action?.id) {
          toast.warning(`Missed node "${lbl}" audio required when category selected`);
          return false;
        }
      }
      if (mode === "voicemail") {
        const dur = Number(node?.data?.max_duration);
        if (!dur || dur < 1 || dur > 60) {
          toast.warning(`Missed node "${lbl}" voicemail duration 1-60s`);
          return false;
        }
      }
      if (mode === "webhook" && !node?.data?.action?.webhook_id) {
        toast.warning(`Missed node "${lbl}" must select a webhook`);
        return false;
      }
      if (mode === "sms_webhook" && !node?.data?.action?.sms_webhook_id) {
        toast.warning(`Missed node "${lbl}" must select an SMS webhook`);
        return false;
      }
    }
    return true;
  }

  for (const node of nodes) {
    const incoming = incomingMap.get(node.id) || [];
    const outgoing = outgoingMap.get(node.id) || [];
    const label = String(node?.data?.label ?? "").toUpperCase();

    const isTransferFullDay =
      node.type === "transferNode" &&
      computeTransferCoverage(node?.data?.timeSlots) >= FULL_DAY_MINS;

    /* Rule 1: End nodes must be Hangup nodes (transfer w/ full-day coverage + defaultNode exempt) */
    if (incoming.length === 0 && outgoing.length === 0 && node.type !== "hangupNode" && node.type !== "defaultNode" && !isTransferFullDay) {
          toast.warning(`Source and Handle of "${label}" must be a Connected to a node`)
      return false;
    }

    /* Rule 2: Input handle must have at least one connection (except answer nodes) */
    if (
      node.type !== "answerNode" &&
      incoming.length === 0
    ) {
      toast.warning(`Node "${label}" must have at least one input connected`)
      return false;
    }

    /* Rule 3: Selected output handles must be connected */
    const selectedHandles = node.data?.selectedHandles;
    if (selectedHandles) {
      for (const [handleId, handleObj] of Object.entries(selectedHandles)) {
        if (!handleObj || Object.keys(handleObj).length === 0) continue;

        const isConnected = outgoing.some(
          edge => edge.sourceHandle === handleId
        );

        if (!isConnected) {
          const dtmf = handleId.split("-")[1];
          toast.warning(`Output handle "DTMF_${dtmf}" of node "${label}" must be connected`)
          return false;
        }
      }
    }



    /* Rule 4: End nodes must be Hangup/Voicemail/Default nodes (transfer w/ full-day coverage exempt) */
    if (
      outgoing.length === 0 &&
      node.type !== "hangupNode" &&
      node.type !== "voicemailNode" &&
      node.type !== "defaultNode" &&
      !isTransferFullDay
    ) {
          toast.warning(`End node "${label}" must be a Hangup, Voicemail, or Default node`)
      return false;
    }

    /* Voicemail node validation */
    if (node.type === "voicemailNode") {
      if (node?.data?.use_media && node?.data?.action?.prompt_category && !node?.data?.action?.id) {
        toast.warning(`Voicemail "${label}" audio required when category selected`);
        return false;
      }
      const dur = Number(node?.data?.max_duration);
      if (!dur || dur < 5 || dur > 60) {
        toast.warning(`Voicemail "${label}" must have duration 5-60s`);
        return false;
      }
    }

    /* Transfer node: if <24h coverage, must have attached default node */
    if (node.type === "transferNode") {
      const slots = node?.data?.timeSlots || [];
      if (slots.length === 0) {
        toast.warning(`Transfer node "${label}" must have at least one time slot`);
        return false;
      }
      const cov = computeTransferCoverage(slots);
      if (cov < FULL_DAY_MINS) {
        const defId = node?.data?.defaultNodeId;
        const defNode = defId ? nodes.find(n => n.id === defId) : null;
        if (!defNode) {
          toast.warning(`Transfer "${label}" requires a default node (open settings and save again)`);
          return false;
        }
      }
    }

    /* Default node validation */
    if (node.type === "defaultNode") {
      const mode = node?.data?.mode;
      if (!mode) {
        toast.warning(`Default node attached to "${node?.data?.parent || ""}" must have an action`);
        return false;
      }
      if (mode === "hangup_with_media") {
        if (node?.data?.action?.prompt_category && !node?.data?.action?.id) {
          toast.warning(`Default node audio required when category selected`);
          return false;
        }
      }
      if (mode === "voicemail") {
        if (node?.data?.use_media && node?.data?.action?.prompt_category && !node?.data?.action?.id) {
          toast.warning(`Default node audio required when category selected`);
          return false;
        }
        const dur = Number(node?.data?.max_duration);
        if (!dur || dur < 5 || dur > 60) {
          toast.warning(`Default node voicemail must have duration 5-60s`);
          return false;
        }
      }
    }



    /* Rule 5: Play node must have audio */
    if (node.type === "playNode" && !node?.data?.action?.audio) {
          toast.warning(`Node "${label}" must select an audio file`)
      return false;
    }

        /* Rule 6: Record node must have audio ad record_duration*/
        if (node.type === "recordNode") {
            const action = node?.data?.action || {};
            const duration = action?.duration;

            if (!action?.audio) {
                toast.error(`Node "${label}" must select an audio file`);
                return false;
            }

            if (!duration || Number(duration) <= 0) {
                toast.error(`Node "${label}" must have a valid record duration (positive integer)`);
                return false;
            }
        }

    /* Rule 7: Gather node must have menu audio */
    if (
      node.type === "gatherNode" &&
      !node?.data?.action?.menu?.audio
    ) {
          toast.warning(`Node "${label}" must select an audio file`)
      return false;
    }
  }

  return true;
};